
import { Component, Watch, Vue } from "vue-property-decorator";
import DateTimePicker from "@/components/DatetimePicker.vue";
import Passenger from "@/components/Passenger/models/Passenger";
import PassengerDestination from "@/components/Passenger/models/PassengerDestination";
import Destination from "@/components/Passenger/models/Destination";
import WeekDay from "../models/WeekDay";
import Ride, { RideType } from "../models/Ride";
import Timetable from "../models/Timetable";
import { Guid } from "guid-typescript";
import vuetifyToast from 'vuetify-toast'
import PassengerList from "@/components/Passenger/components/PassengerList.vue";
import Helpers from '../../../util/Helpers'
import TimeOfDayEditor from '../components/TimeOfDayEditor.vue';
import moment from 'moment';
import { TimeOfDay } from '@/components/Timetable/models/TimeOfDay';
import { TimetableEditor } from '@/components/Timetable/components/TimetableEditor.vue';
import { PassengerDialog } from '@/components/Passenger/components/PassengerDialog.vue';
import GroupPicker from "@/components/Passenger/components/GroupPicker.vue"
import { Group } from '@/components/Passenger/models/Group';
import Routes from '@/Routes';
import { RolePermission } from '@/models/Role';
import TimetablePassenger from '@/components/Timetable/models/TimetablePassenger';
import TimetableState, { TimetableStates } from '@/components/Timetable/models/TimetableStates';
import TransportationPermission from '@/components/Passenger/models/TransportationPermission';
import restService from '@/services/restService';
import { ScheduleItemDirectionEnum } from '@/components/Schedule/enums';
import { IScheduleItem } from '@/typings/timit';

@Component({
  components: {
    PassengerList,
    DateTimePicker,
    TimeOfDayEditor,
    GroupPicker
  }
})
export class TimetableView extends Vue {
  search: string = "";
  groupNamesToFilterIn: string[] = [];
  splitDatePickerVisible = false;
  splitDate = null;
  templateStartsTimeOfDay: TimeOfDay = new TimeOfDay(9, 0);
  templateEndsTimeOfDay: TimeOfDay = new TimeOfDay(14, 0);
  isMounted: boolean = false;
  datatable: any = null;
  updateDatatableRefInterval: any = null;
  searchDebounceTimeout: any = null;
  isSplitting: boolean = false;
  editedRide: Ride = new Ride({ timeOfDay: new TimeOfDay(9, 0)});
  showTimeOfDayEditor: boolean = false;
  focusTimeOfDayTimeout: number = 0;
  orderInProgress: boolean = false;
  cancelOrderInProgress: boolean = false;

  pagination: any = {
    sortBy: 'passengerName',
    rowsPerPage: -1,
  }
  selected: any[] = [];

  get isLoading(): boolean {
    return this.$store.state.timetable.pending.timetables ||
      this.$store.state.ride.pending.getRides
  }

  get passengersLoading(): boolean {
    return this.$store.state.passenger.pending.timetablePassengers;
  }

  get helpers() {
    return Helpers;
  }

  get filteredPassengers(): Passenger[]{
    return this.datatable
      ? this.datatable.filteredItems.map(i => i.passenger) as Passenger[]
      : [];
  }

  get selectedFilteredPassengers(): Passenger[]{
    return this.filteredPassengers.filter((p: Passenger) => p.selected);
  }

  get allFilteredPassengersSelected(): boolean {
    return this.filteredPassengers.every((p: Passenger) => p.selected);
  }

  set allFilteredPassengersSelected(value) {
    this.onToggleAll(value);
  }

  get routeTimetableId(): Guid {
    return this.$store.state.route.params.timetableId;
  }

  get timetable(): Timetable {
    return this.storeTimetable;
  }

  get destination(): Destination {
    return this.destinations.find(d => d.id === this.timetable.destinationId);
  }

  get storeTimetable(): Timetable {
    return this.$store.state.timetable.timetables.find(t =>
      t.id === this.routeTimetableId
    );
  }

  get canDeletePassengers() {
    // Can delete passengers from timetable only if timetables state is new
    return this.timetable.state === TimetableStates.New.id;
  }

  get timetableGroups(): any[] {
    const groups = [];

    this.storeTimetablePassengers.forEach((p: Passenger) => {
      if (p.groups) {
        p.groups.split(",").forEach(g => {
          groups.push({name: g.trim()});
        });
      }
    })
    return groups;
  }

  get weekDays(): WeekDay[] {
    return WeekDay.WeekDays(
      this.$store.state.user.weekStartsOnSunday,
      this.timetable.startDate,
      this.timetable.endDate
    );
  }

  get visibleWeekDays(): WeekDay[] {
    const visibleWeekDays = this.weekDays.filter(wd => (!wd.weekend || this.$store.state.user.showWeekends));
    return visibleWeekDays;
  }

  get passengerWeekdayRides() {
    const passengerWeekdayRides = this.filteredTimetablePassengers.map(passenger => {
      const r1 = {
        selected: false,
        passenger,
        passengerName: passenger.name,
        passengerGroups: passenger.groups,
        weekDays: this.visibleWeekDays.map(wd => {
          // Ride.sortByTime() has been disabled, it's unneeded and affects performance
          const rides = this.timetableRides.filter(
            r => r.passengerId === passenger.id  && r.weekDayId === wd.id
          );
          const arrivals = rides.filter(r => r.fromHome);
          const departures = rides.filter(r => r.toHome);
          const r2 = {
            weekDay: wd,
            arrival: arrivals.length > 0 ? arrivals[0] : undefined,
            departure: departures.length > 0 ? departures[0] : undefined
          }
          return r2;
        })
      }
      return r1;
    });
    return passengerWeekdayRides;
  }

  get visibleHeaders(): any[] {
    return [
      {
        text: this.$i18n.translate('passenger.prop.name.label', 'Name'),
        align: 'left',
        value: 'passengerName'
      },
      {
        text: this.$i18n.translate('passenger.prop.tags.label', 'Tags'),
        align: 'left',
        value: 'passengerGroups'
      },
      ...this.visibleWeekDays.map(
        wd => (
          {
            text: this.$i18n.translate('weekday.' + wd.name.toLowerCase(), wd.name ),
            weekDayId: wd.id,
            weekend: wd.weekend,
            sortable: false,
            enabled: wd.enabled
          }
        )
      )
    ];
  }

  get selectedWeekDay() {
    const val =  this.weekDays.find(wd => wd.id === parseInt(this.$store.state.route.params.weekDayId, 10));
    return val;
  }

  selectPassengerWeekDay(passenger: Passenger, weekDay: WeekDay) {
    this.$router.push({
      name: weekDay ? 'TimetablePassengerWeekday' : 'TimetablePassenger',
      params: {
        timetableId: this.timetable.id.toString(),
        passengerId: passenger.id.toString(),
        weekDayId: weekDay ? weekDay.id.toString() : null,
      }
    });
  }

  get routeDestinationId(): number {
    return parseInt(this.$store.state.route.params.destinationId, 10);
  }

  @Watch("routeDestinationId")
  onRouteDestinationIdChanged(val, oldVal) {
    this.$router.push({ name: Routes.timetableListRoute.name });
  }

  get me() {
    return this.$store.state.user.me;
  }

  get userCanEditTimetables(): boolean {
    return this.me
      ? this.me.hasPermissions(this.routeDestinationId, [RolePermission.UpdateTimetables])
      : false;
  }

  get storeRides(): Ride[]{
    return this.$store.state.ride.rides;
  }

  get timetableRides(): Ride[] {
    const timetableRides = this.storeRides.filter(r => r.timetableId === this.timetable.id);
    return timetableRides;
  }

  getTimetableRidesForPassenger(passenger: Passenger): Ride[] {
    return this.timetableRides.filter((ride: Ride) => {
      return ride.passengerId === passenger.id;
    });
  }

  getRidesForPassengerDay(passenger: Passenger, weekDayId: number): Ride[] {
    return this.timetableRides.filter((ride: Ride) => {
      return ride.passengerId === passenger.id
        && ride.weekDayId === weekDayId;
    });
  }

  async onDeletePassengersClick() {
    if (confirm(this.$t('timetable.passengers.delete-selected-passengers.question'))) {
      const passengers = [...this.selectedFilteredPassengers];
      const passengerIds = passengers.map(p => p.id)
      this.timetable.removePassengerIds(passengerIds);
      await this.saveTimetable(this.timetable);
      passengers.forEach(async (p) => {
        const rides = this.getTimetableRidesForPassenger(p);
        rides.forEach(r => r.deleting = true);
        await this.$store.dispatch('ride/deleteRides', { data: rides.map((r: Ride) => r.id) });
      })
    }
  }

  async onDestinationIdChanged(e) {
    this.timetable.destinationId = e;
    await this.saveTimetable(this.timetable);
  }

  async saveTimetable(timetable: Timetable) {
    await this.$store.dispatch('timetable/updateTimetable',
      { params: { id: timetable.id}, data: Timetable.toApi(timetable) });
  }

  get minAllowedSplitDate(): Date {
    const startMoment = moment(this.timetable.startDateString).add(0, "days");
    return startMoment.toDate();
  }

  get minAllowedSplitDateStamp(): number {
    return this.minAllowedSplitDate.getTime();
  }

  get maxAllowedSplitDate(): Date {
    const endMoment = moment(this.timetable.endDateString);
    return endMoment.toDate();
  }

  get maxAllowedSplitDateStamp(): number {
    return this.maxAllowedSplitDate.getTime();
  }

  get tomorrowDateStamp(): number {
    return moment().add(1, "days").toDate().getTime();
  }

  allowedSplitDates(val) {
    const valDate = new Date(val);
    const valDateStamp = valDate.getTime();
    const day = valDate.getDay();
    const isWeekend = (day === 6) || (day === 0);
    return (!isWeekend || this.$store.state.user.showWeekends)
            && valDateStamp > this.minAllowedSplitDateStamp
            && valDateStamp < this.maxAllowedSplitDateStamp
            && valDateStamp > this.tomorrowDateStamp;
  }

  get timetablePassengerIds(): number[] {
    if (!this.timetable) {
      return [];
    }

    if (!this.timetable.timetablePassengers || this.timetable.timetablePassengers.length === 0) {
      if (this.timetableRides.length > 0) {
        const passengerIds: number[] = this.timetableRides.map(ride => ride.passengerId);
        const uniquePassengerIds: number[] = [...new Set(passengerIds)];
        this.timetable.timetablePassengers = uniquePassengerIds.map(
          pid => new TimetablePassenger(this.timetable.id, pid)
        )
      }
    }
    return this.timetable.timetablePassengers
      .map(tp => tp.passengerId);
  }

  @Watch("timetablePassengerIds", {immediate: true})
  async timetablePassengerIdsChanged(val, oldVal) {
    await this.getTimetablePassengersFromApi();
  }

  async getTimetablePassengersFromApi() {
    await this.$store.dispatch(
      'passenger/getTimetablePassengers',
      {
        data: this.timetablePassengerIds
      }
    );
  }

  get storeTimetablePassengers(): Passenger[] {
    return this.$store.state.passenger.timetablePassengers;
  }

  async onPickPassengers(pickedPassengers: Passenger[]) {
    this.timetable.addPassengerIds(pickedPassengers.map((p: Passenger) => p.id));
    this.saveTimetable(this.timetable);
    this.showPassengerList = false;
  }

  get queryCreated(): boolean {
    return this.$route.query.created === "true";
  }

  get queryAddPeople(): boolean {
    return this.$route.query.addpeople === "true";
  }

  get showPassengerList(): boolean {
    return this.queryAddPeople;
  }

  set showPassengerList(value: boolean) {
    const query = value ? { addpeople: "true" } : {};
    this.$router.replace({ query })
  }

  onCancelPick(pickedPassengers: Passenger[]) {
    this.showPassengerList = false;
  }

  get filteredTimetablePassengers(): Passenger[] {
    const passengers = this.storeTimetablePassengers.filter((p: Passenger) => {
      if (!this.groupNamesToFilterIn || this.groupNamesToFilterIn.length === 0) {
        return true;
      }
      if (p.groups) {
        const passengerGroups = p.groups.split(",").map(g => g.trim());
        return passengerGroups.some(g => this.groupNamesToFilterIn.includes(g));
      }
      return false;
    });
    return passengers;
  }

  set filteredTimetablePassengers(value) {
    console.log("NO-OP: passengers set:", value);
  }

  get transportPermissions() {
    const permissions: TransportationPermission[] = this.$store.state.destination.transportPermissions;
    return permissions;
  }

  get destinations(): Destination[] {
    return this.$store.state.destination.destinations;
  }

  get RideType() {
    return RideType;
  }

  get timetableIsLocked() {
    return this.timetable.isLocked;
  }

  get timetableStarted() {
    const momentNow = moment();
    const momentStart = moment(this.timetable.startDateString);
    return momentNow.isSameOrAfter(momentStart);
  }

  get timetableDaysUntilStart() {
    const momentNow = moment();
    const momentStart = moment(this.timetable.startDateString);
    return momentStart.diff(momentNow, 'days');
  }

  get timetableIsAccepted() {
    return this.timetable.isAccepted;
  }

  get timetableIsOrdered() {
    return this.timetable.isOrdered;
  }

  get timetableIsOrderable() {
    return this.timetable.isOrderable && this.timetableHasRides;
  }

  get timetableHasRides() {
    return this.timetableRides.length > 0;
  }

  get timetableOrderIsCancelable() {
    return this.timetable.isOrderIsCancelable;
  }

  get timetableState(): TimetableState {
    return TimetableStates.States.find(s => s.id === this.timetable.state);
  }

  get timetableStateNameLowerCase(): string {
    return this.timetableState ? this.timetableState.nameLowerCase : "";
  }

  get timetableStateName(): string {
    return this.timetableState ? this.timetableState.name : "";
  }

  get splitAllowed() {
    // Splitting (copying) is currently disabled completely
    return false;
  }

  get splitButtonTitle() {
    if (this.splitAllowed) {
      return this.$i18n.translate(
        'timetable.split.button_title',
        'Split the timetable to two separate parts'
      );
    } else {
      return this.splitDisabledReason;
    }
  }

  get splitDisabledReason() {
    return this.$i18n.translate(
      'timetable.split.disabled.button_title',
      'You can only split if at least {days} day(s) remain until the end of the timetable',
      {
        days: this.$store.state.app.timetable.splitLimitDays.allowDaysBeforeEnd
      }
    )
  }

  get passengersRequiredPermission() {
    const passengersPermisionsRequired = this.filteredTimetablePassengers.filter(pas => pas.transportationPermissionRequired);
    return passengersPermisionsRequired;
  }

  get passengersWhitoutPermissions() {
    const startDate = moment(this.timetable.startDateString).toDate();
    const endDate = moment(this.timetable.endDateString).toDate();
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const permissionBetweenDates = this.transportPermissions.filter(tp => {
      const s = new Date(tp.startDate);
      const e = new Date(tp.endDate);
      s.setHours(0, 0, 0, 0)
      e.setHours(0, 0, 0, 0)
      if (startDate.getTime() >= s.getTime() &&  endDate.getTime() <= e.getTime()) {
        return true;
      }
      return false;
    });

    const passengersWitOutPermission = this.passengersRequiredPermission
      .filter(p => !permissionBetweenDates.some(tp => tp.passengerId === p.id));
    return passengersWitOutPermission;
  }

  onTimetableEditClick() {
    this.$root.$emit(TimetableEditor.EVENT_TIMETABLE_EDITOR_OPEN, this.timetable);
  }

  onTimetableSplitClick() {
    const endMoment = moment(this.timetable.endDateString);
    const nowMoment = moment();
    const nowToEndDuration = moment.duration(endMoment.diff(nowMoment));
    const nowToEndDays = nowToEndDuration.asDays();
    this.splitDate = nowMoment.add(Math.ceil(nowToEndDays / 2), "days").format("YYYY-MM-DD");
    this.splitDatePickerVisible = true;
  }

  async splitTimetable(splitDate) {
    this.isSplitting = true;
    // Hide the dialog
    this.splitDatePickerVisible = false;
    // TODO: Make all the add/update operations in a single transaction to enable rollback
    const newTimetable = this.timetable.split(splitDate);

    // Save old
    await this.$store.dispatch('timetable/updateTimetable', {
      params: {
        id: this.timetable.id
      },
      data: Timetable.toApi(this.timetable)
    });
    // Save new
    const response = await this.$store.dispatch('timetable/createTimetable', { data: newTimetable });
    const newTimetableFromStoreId = response.data.id;

    // Copy rides
    const newRides = this.timetableRides.map(r => {
      const ride = Ride.CopyFrom(r);
      ride.timetableId = newTimetableFromStoreId;
      return ride;
    });

    // Change route
    this.$router.push({
      name: Routes.timetableRoute.name,
      params: {
          timetableId: newTimetableFromStoreId.toString()
      }
    });

    this.isSplitting = false;

    // Save the copied ride
    await this.$store.dispatch(
      'ride/createRides',
      {
        data: newRides.map((r: Ride) => Ride.toApiRide(r))
      }
    );
  }

  onAddPassengersClick() {
    this.showPassengerList = true;
  }

  onGroupFilterGroupPickerChange(selectedGroups: Group[]) {
    this.groupNamesToFilterIn = selectedGroups.map(g => g.name);
  }

  onPassengerClick(event: Event, passenger: Passenger) {
    this.$root.$emit(
      PassengerDialog.EVENT_PASSENGER_DIALOG_SHOW,
      passenger,
      {
        timetable: this.timetable,
        rides: this.getTimetableRidesForPassenger(passenger)
      }
    )
  }

  async onRideCellClick(e: Event, passenger: Passenger, weekDay: WeekDay, ride: Ride, rideType: RideType) {
    if (passenger.inactive) {
      return;
    }

    if (!this.userCanEditTimetables) {
      console.warn("[Timit] Permissions: User does not have permission to edit timetables");
      vuetifyToast.error(
        this.$i18n.translate(
          "permission.missing.editTimetables",
          "Your user account does not have a permission to edit timetables."
        )
      );
      return;
    }

    if (this.timetableIsLocked) {
      console.warn("[Timit] Timetable is locked, cannot edit or add rides.");
      vuetifyToast.error(this.$i18n.translate("timetable.locked.error", "Timetable is locked"));
      return;
    }

    const templateTimeOfDay = rideType === RideType.Arrival
      ? this.templateStartsTimeOfDay
      : this.templateEndsTimeOfDay;

    let editedRide: Ride = ride;

    if (!editedRide) {
      // User clicked on an empty cell
      // Create a new ride
      editedRide = new Ride(
        {
          timeOfDay: new TimeOfDay(templateTimeOfDay.hours, templateTimeOfDay.minutes),
          passengerId: passenger.id,
          timetableId: this.timetable.id,
          fromHome: rideType === RideType.Arrival,
          toHome: rideType === RideType.Departure,
          fromId: rideType === RideType.Arrival ? 0 : this.destination.id,
          toId: rideType === RideType.Arrival ? this.destination.id : 0,
          weekDayId: weekDay.id,
          rideType
        }
      )
      await this.$store.dispatch('ride/createRides', {data: [Ride.toApiRide(editedRide)]});
    }

    // Assign the ride to the editor
    this.editedRide = editedRide;

    // Remove the editor from the current parent
    const editorElement = (this.$refs.timeOfDayEditorRef as any).$el;
    const originalParent =  editorElement.parentNode;
    originalParent.removeChild(editorElement);

    // Move the editor to the target cell
    const target = (e.target as Element).closest('.time-of-day-container')
    target.appendChild(editorElement);

    // Hide the display
    const timeOfDayDisplay: Element = target.querySelector('.time-of-day-display');

    // Show the editor
    // Focus and select the editor field
    if (this.focusTimeOfDayTimeout) {
      clearTimeout(this.focusTimeOfDayTimeout);
    }

    this.focusTimeOfDayTimeout = window.setTimeout(() => {
      document.querySelectorAll('.time-of-day-editor.editor')
        .forEach((elem: Element) => elem.classList.remove('hidden'));

      timeOfDayDisplay.classList.add('hidden');
      const input = target.querySelector('input');
      input.focus();
      input.select();
    }, 200);
  }

  onTimeOfDayEditorBlur() {
    // Hide timeOfDayEditor
    document.querySelectorAll('.time-of-day-editor.editor')
      .forEach((elem: Element) => elem.classList.add('hidden'));

    // Show timeOfDayDisplay(s)
    document.querySelectorAll('.time-of-day-display.hidden')
      .forEach((elem: Element) => elem.classList.remove('hidden'));
  }

  async onRideUpdated(ride: Ride) {
    if (ride.timeOfDay.empty) {
      ride.deleting = true;
      this.$store.dispatch('ride/deleteRides', { data: [ride.id] });
      return;
    }

    if (ride.isValid) {
      await this.$store.dispatch('ride/updateRide', { params: { id: ride.id}, data: Ride.toApiRide(ride) });
    }
  }

  onToggleAll(value) {
    this.filteredPassengers.filter(p => !p.inactive).forEach((p: Passenger) => {
      p.selected = value;
    });
  }

  onChangeSort(column) {
    if (this.pagination.sortBy === column) {
      this.pagination.descending = !this.pagination.descending;
    } else {
      this.pagination.sortBy = column;
      this.pagination.descending = false;
    }
  }

  onSearchInput(val) {
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
    this.searchDebounceTimeout = setTimeout(
      () => {
        this.search = val;
      },
      500
    )
  }

  async onCancelOrderTimetableClick() {
    // TODO: Send the order cancellation, don't just update the state, let the api update the state
    // TODO: Do we need a state OrderCancelled?
    this.cancelOrderInProgress = true;
    try {
      const apiTimetable = Timetable.toApi(this.timetable);
      apiTimetable.state = TimetableStates.New.id;
      await this.saveTimetable(apiTimetable);
      vuetifyToast.success(this.$i18n.translate("timetable.order.canceled.notification", "Order canceled."));
    } catch (err) {
      vuetifyToast.error(
        this.$i18n.translate(
          "timetable.order.canceled.error",
          "Error when canceling order: {errorMessage}",
          {errorMessage: (err as any).message}
        )
      );
    }
    this.cancelOrderInProgress = false;
  }

  async onOrderTimetableClick() {
    if (this.filteredPassengers.some(p => p.inactive)) {
      vuetifyToast.error(this.$i18n.translate(
        "timetable.order.error.contains_inactive_passengers",
        "Please remove inactive passengers from the list before ordering.")
      );
      return;
    }
    this.orderInProgress = true;
    try {
      const apiTimetable = Timetable.toApi(this.timetable);
      apiTimetable.state = TimetableStates.Ordered.id;
      await this.saveTimetable(apiTimetable);
      vuetifyToast.success(this.$i18n.translate("timetable.order.sent.notification", "Order sent."));
    } catch (err) {
      vuetifyToast.error(
        this.$i18n.translate(
          "timetable.order.sent.error",
          "Error when sending order: {errorMessage}",
          {errorMessage: (err as any).message}
        )
      );
    }

    this.orderInProgress = false;
  }

  async onFetchSchedulesClick() {
    const ridesToCreateOrUpdate: Ride[] = [];
    const availableWeekdays = this.weekDays.filter(wd => wd.enabled).map(wd => wd.id);
    const passengers = this.selectedFilteredPassengers.filter(p => !p.inactive);

    for (const passenger of passengers) {
      const schedules = await restService.schedules.getPassengerSchedules(passenger.id);
      const currentSchedule = schedules
        .find(s => new Date(s.startDate) <= this.timetable.startDate &&
          new Date(s.endDate) > this.timetable.startDate);

      if (!currentSchedule) {
        continue;
      }

      const passengerRides = this.timetableRides.filter(tr => tr.passengerId === passenger.id);

      const scheduleItemDirectionMatchesRideType = (itemDirection: ScheduleItemDirectionEnum, rideType: RideType) => {
        if (itemDirection === ScheduleItemDirectionEnum.Start && rideType === RideType.Arrival) {
          return true;
        }
        if (itemDirection === ScheduleItemDirectionEnum.End && rideType === RideType.Departure) {
          return true;
        }
        return false;
      }

      const usableScheduleItems = currentSchedule.scheduleItems.filter(si => availableWeekdays.includes(si.dayOfWeek));

      usableScheduleItems.forEach(si => {
        const scheduleItemRide = passengerRides
          .find(r => r.weekDayId === si.dayOfWeek && scheduleItemDirectionMatchesRideType(si.direction, r.rideType));

        if (scheduleItemRide) {
          scheduleItemRide.timeOfDayUpdate = TimeOfDay.fromMinutesFromMidnight(si.time);
          scheduleItemRide.updating = true;
          ridesToCreateOrUpdate.push(scheduleItemRide);
        } else {
          ridesToCreateOrUpdate.push(this.createRideFromScheduleItem(si, passenger.id));
        }
      });
    }

    if (ridesToCreateOrUpdate.length) {
      const apiRides = ridesToCreateOrUpdate.map((r: Ride) => Ride.toApiRide(r));
      this.$store.dispatch('ride/createOrUpdateRides', { data: apiRides });
    }
  }

  createRideFromScheduleItem(scheduleItem: IScheduleItem, passengerId: number) {
    const getRideType = (direction: ScheduleItemDirectionEnum) => {
      switch (direction) {
        case ScheduleItemDirectionEnum.Start:
          return RideType.Arrival;
        case ScheduleItemDirectionEnum.End:
          return RideType.Departure;
        default:
          return RideType.Unknown;
      }
    }

    return new Ride({
      timeOfDay: TimeOfDay.fromMinutesFromMidnight(scheduleItem.time),
      passengerId,
      timetableId: this.timetable.id,
      fromHome: scheduleItem.direction === ScheduleItemDirectionEnum.Start,
      toHome: scheduleItem.direction === ScheduleItemDirectionEnum.End,
      fromId: scheduleItem.direction === ScheduleItemDirectionEnum.End ? this.timetable.destinationId : 0,
      toId:  scheduleItem.direction === ScheduleItemDirectionEnum.Start ? this.timetable.destinationId : 0,
      weekDayId: scheduleItem.dayOfWeek,
      rideType: getRideType(scheduleItem.direction),
      creatingId: Guid.create(),
      creating: true,
    });
  }

  async updateStartsOrEnds(
    weekDayId: number,
    rideType: RideType,
    fromHome: boolean,
    toHome: boolean,
    fromId: number,
    toId: number,
    templateTimeOfDay: TimeOfDay
  ) {
    const ridesToCreateOrUpdate: Ride[] = [];
    let ridesToDelete: Ride[] = [];

    this.selectedFilteredPassengers.filter(p => !p.inactive).forEach(p => {
      const currentRidesForPassengerWeekday: Ride[] = this.getRidesForPassengerDay(p, weekDayId)
        .filter(r => r.rideType === rideType && r.fromHome === fromHome && r.toHome === toHome);

      if (templateTimeOfDay.empty) {
        // The time template is empty, meaning we should delete the rides for the day for the passenger
        ridesToDelete = ridesToDelete.concat(currentRidesForPassengerWeekday);
        return;
      }

      // Clean up, remove all other rides except for the first one for the day
      if (currentRidesForPassengerWeekday.length > 1) {
        ridesToDelete = ridesToDelete.concat(
          currentRidesForPassengerWeekday.slice(1, currentRidesForPassengerWeekday.length)
        );
      }

      const firstRideForPassengerWeekDay: Ride = currentRidesForPassengerWeekday
        ? currentRidesForPassengerWeekday[0]
        : undefined;

      if (firstRideForPassengerWeekDay) {
        // Found an existing ride for the same day, will update
        if (firstRideForPassengerWeekDay.timeOfDay.matches(templateTimeOfDay)) {
          // Time of day is the same, no need to update this day for this passenger
          return;
        }
        // Set an updated time of day for an existing ride
        // (timeOfDayUpdate will be visible in UI until API returns a real updated object with timeOfDay)
        firstRideForPassengerWeekDay.timeOfDayUpdate = new TimeOfDay(
          templateTimeOfDay.hours,
          templateTimeOfDay.minutes
        )
        firstRideForPassengerWeekDay.updating = true;
        ridesToCreateOrUpdate.push(firstRideForPassengerWeekDay);
        return;
      }

      // No existing ride for the weekday for the passenger was found, create one
      ridesToCreateOrUpdate.push(
        new Ride(
          {
            timeOfDay: new TimeOfDay(templateTimeOfDay.hours, templateTimeOfDay.minutes),
            passengerId: p.id,
            timetableId: this.timetable.id,
            fromHome,
            toHome,
            fromId,
            toId,
            weekDayId,
            rideType,
            creatingId: Guid.create(),
            creating: true
          }
        )
      );
    });

    if (ridesToDelete.length) {
      ridesToDelete.forEach(r => r.deleting = true);
      this.$store.dispatch('ride/deleteRides', { data: ridesToDelete.map((r: Ride) => r.id) });
    }

    if (ridesToCreateOrUpdate.length) {
      this.$store.dispatch('ride/createOrUpdateRides', { data: ridesToCreateOrUpdate.map((r: Ride) => Ride.toApiRide(r)) });
    }
  }

  async onStartsClicked(weekDayId: number) {
    await this.updateStartsOrEnds(weekDayId, RideType.Arrival, true, false,
      0, this.timetable.destinationId, this.templateStartsTimeOfDay)
  }

  async onEndsClicked(weekDayId: number) {
    await this.updateStartsOrEnds(weekDayId, RideType.Departure, false, true,
      this.timetable.destinationId, 0, this.templateEndsTimeOfDay)
  }

  updateDatatableRef() {
    this.datatable = (this.$refs.datatable as any);
    if (this.datatable) {
      if (this.updateDatatableRefInterval) {
        clearInterval(this.updateDatatableRefInterval);
      }
      if (this.queryCreated) {
        this.allFilteredPassengersSelected = true;
      }
      this.isMounted = true;
    }
  }

  get isTransportationPermissionEnabled(): boolean {
    return this.me.hasPermissions(this.routeDestinationId, [RolePermission.ReadTransportationPermissions]) ||
      this.me.hasPermissions(this.routeDestinationId, [RolePermission.CreateTransportationPermissions]);
  }

  missingTripPermission(passId: number): boolean {
    if (this.passengersWhitoutPermissions.some(p => p.id === passId)) {
      return true;
    }
    return false;
  }

  mounted() {
    const startTime = localStorage.getItem('templateStartsTimeOfDay')?.toString();
    const endTime = localStorage.getItem('templateEndsTimeOfDay')?.toString();

    if (startTime) {
      this.templateStartsTimeOfDay.value = startTime;
    }
    if (endTime) {
      this.templateEndsTimeOfDay.value = endTime;
    }

    // Check if datatable has now mounted
    this.updateDatatableRefInterval = setInterval(this.updateDatatableRef, 600);
    // Temp fix to scrolling issue
    const appContainer = document.querySelector('.app-content-container')
    if (appContainer) {
      appContainer.scrollTop = 0
    }
  }

  onBackToListClick() {
    this.$router.push({
      name: Routes.timetablesRoute.name
    })
  }

  beforeDestroy() {
    // TODO: Temporary workaround for selection sharing bug, handle this better
    this.storeTimetablePassengers
      .filter((p: Passenger) => !p.selected)
      .forEach((p: Passenger) => p.selected = true);

    // Clear intervals and timeouts
    if (this.updateDatatableRefInterval) {
      clearInterval(this.updateDatatableRefInterval);
    }

    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
  }

  templateStartsTimeOfDayChanged() {
    localStorage.setItem('templateStartsTimeOfDay', this.templateStartsTimeOfDay.stringValue);
  }

  templateEndsTimeOfDayChanged() {
    localStorage.setItem('templateEndsTimeOfDay', this.templateEndsTimeOfDay.stringValue);
  }
}

export default TimetableView;
